import './styles.scoped.css'
import {useState, useEffect, useRef} from 'react'
import {
    Typography,
    TextField,
    Select,
    InputAdornment,
    MenuItem,
    IconButton,
    Chip,
    Menu,
    FormControl,
    Checkbox,
    ListItemText,
    Skeleton,
    Avatar,
    CircularProgress,
    Box,
    Tooltip,
    Button,
    FormControlLabel,
} from '@mui/material'
import {Search, MoreVert, FilterAlt, Warning} from '@mui/icons-material'
import {DataGrid} from '@mui/x-data-grid'
import {DataGridFooter} from '../../../../components'
import {scheduleApi} from '../../../../services'
import {format} from 'date-fns'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {useNavigate} from 'react-router-dom'
import {useMobile} from '../../../../hooks'
import {getInitials, getColorFromName, parseError, ignoreTimezone, formatTimeDiffShort, formatTime} from '../../../../utils/helpers'
import InfiniteScroll from 'react-infinite-scroll-component'
import {
    MarkAsComplete,
    DeleteVisits,
    CancelVisits,
    RescheduleVisits,
    ClockOut,
    UndoClockIn,
    SendSignatureReminder,
    ChangeCaregiver,
    TableSkeleton,
    SkeletonContainer,
    TextSkeleton,
    ChipSkeleton,
    AvatarSkeleton,
} from '../../../../components'
import {MobileFilter} from '..'
import {DatePicker as DatePickerAntd} from 'antd'
import {noResultsFound} from '../../../../assets'

// Mirrors the mobile visit card below.
const VisitCardsSkeleton = () => (
    <SkeletonContainer>
        {['46%', '38%', '52%'].map((width, index) => (
            <div className="card" key={index}>
                <div className="top-card">
                    <div className="left-card">
                        <ChipSkeleton width={96} height={32} />
                    </div>
                    <div className="right-card">
                        <Skeleton variant="circular" width={24} height={24} sx={{mr: '8px'}} />
                    </div>
                </div>
                <TextSkeleton width={width} />
                <div className="form">
                    <TextSkeleton width={72} fontSize={15} />
                    <TextSkeleton width={40} />
                </div>
                <TextSkeleton width={48} fontSize={15} />
                <div className="people-card">
                    <AvatarSkeleton size={32} />
                    <div className="people-profile-card">
                        <TextSkeleton width={120} />
                        <TextSkeleton width={160} />
                    </div>
                </div>
                <TextSkeleton width={72} fontSize={15} />
                <div className="people-card">
                    <AvatarSkeleton size={24} />
                    <TextSkeleton width={110} />
                </div>
                <div className="form" style={{marginTop: 4}}>
                    <div style={{width: 180}}>
                        <TextSkeleton width={104} fontSize={15} />
                    </div>
                    <TextSkeleton width={92} fontSize={15} />
                </div>
                <div className="form" style={{marginTop: -4}}>
                    <div style={{width: 180}}>
                        <TextSkeleton width={80} />
                        <TextSkeleton width={150} />
                    </div>
                    <div>
                        <TextSkeleton width={80} />
                        <TextSkeleton width={110} />
                    </div>
                </div>
            </div>
        ))}
    </SkeletonContainer>
)

export default ({status, clients, services, startDate, setStartDate, endDate, setEndDate}) => {
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const [rows, setRows] = useState([])
    const [rowCount, setRowCount] = useState(0)
    const [sortBy, setSortBy] = useState(null)
    const [sortOrder, setSortOrder] = useState(null)
    const [search, setSearch] = useState(null)
    const [searchTyping, setSearchTyping] = useState(null)
    const {setLoading, setMessageBox, setMobileSidebar, branchId} = useGlobalStates()
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedRow, setSelectedRow] = useState(null)
    const [selectedRows, setSelectedRows] = useState([])
    const [openMobileFilter, setOpenMobileFilter] = useState(false)
    const [hasMoreScroll, setHasMoreScroll] = useState(true)
    const [scrollStep, setScrollStep] = useState(1)
    const [scrollData, setScrollData] = useState([])
    const [openMarkAsComplete, setOpenMarkAsComplete] = useState(false)
    const [openDeleteVisits, setOpenDeleteVisits] = useState(false)
    const [openCancelVisits, setOpenCancelVisits] = useState(false)
    const [openRescheduleVisits, setOpenRescheduleVisits] = useState(false)
    const [openClockOut, setOpenClockOut] = useState(false)
    const [openUndoClockIn, setOpenUndoClockIn] = useState(false)
    const [openSendSignatureReminder, setOpenSendSignatureReminder] = useState(false)
    const [openChangeCaregiver, setOpenChangeCaregiver] = useState(false)
    const [selectedClients, setSelectedClients] = useState([])
    const [searchClientTerm, setSearchClientTerm] = useState('')
    const filteredClients = clients?.filter(item => item?.display_name?.toLowerCase().includes(searchClientTerm?.toLowerCase()))
    const [selectedServices, setSelectedServices] = useState([])
    const [searchServiceTerm, setSearchServiceTerm] = useState('')
    const filteredServices = services?.filter(item => item?.display_name?.toLowerCase().includes(searchServiceTerm?.toLowerCase()))
    const [skeleton, setSkeleton] = useState(true)
    const requestId = useRef(0)
    const scrollRequestId = useRef(0)
    // Set when a reload follows an action on visits already on screen: keep them visible behind the spinner.
    const refreshRef = useRef(false)
    const {mobile} = useMobile()
    const navigate = useNavigate()
    const [rowSelectionModel, setRowSelectionModel] = useState({
        type: 'include',
        ids: new Set(),
    })
    const {RangePicker} = DatePickerAntd
    const menuRef = useRef(null)
    const [editable, setEditable] = useState(false)
    const [dateRangePopup, setDateRangePopup] = useState(false)

    useEffect(() => {
        if (mobile) {
            setMobileSidebar(false)
        }
    }, [])

    useEffect(() => {
        if (clients) {
            setSelectedClients(clients.map(r => r.id))
        }
    }, [clients])

    useEffect(() => {
        if (services) {
            setSelectedServices(services.map(r => r.id))
        }
    }, [services])

    useEffect(() => {
        if (rowSelectionModel && rows?.length > 0) {
            const {type, ids} = rowSelectionModel
            if (type === 'include') {
                setSelectedRows(rows.filter(row => ids?.has(row?.id)))
            } else if (type == 'exclude') {
                setSelectedRows(rows.filter(row => !ids?.has(row?.id)))
            }
        }
    }, [rowSelectionModel, rows])

    useEffect(() => {
        if (selectedRows && selectedRows?.length > 1 && !selectedRows.every(item => item?.schedule_id === selectedRows[0]?.schedule_id)) {
            setEditable(false)
        } else {
            setEditable(true)
        }
    }, [selectedRows])

    useEffect(() => {
        const timeout = setTimeout(() => {
            loadData()
        }, 1000)
        return () => clearTimeout(timeout)
    }, [branchId, page, pageSize, sortBy, sortOrder, status, search, selectedClients, selectedServices, startDate, endDate, mobile])

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (mobile && scrollStep == 1) {
                loadScrollData()
            }
        }, 1000)
        return () => clearTimeout(timeout)
    }, [branchId, scrollStep, mobile])

    useEffect(() => {
        const timeout = setTimeout(() => {
            setSearch(searchTyping)
        }, 1000)
        return () => clearTimeout(timeout)
    }, [searchTyping])

    const loadData = (refresh = false) => {
        if (mobile) {
            if (refresh) {
                refreshRef.current = true
                setLoading(true)
                if (scrollStep == 1) {
                    loadScrollData()
                } else {
                    scrollRequestId.current++
                    setScrollStep(1)
                }
                return
            }
            if (scrollStep != 1) {
                scrollRequestId.current++
                setSkeleton(true)
            }
            setScrollData([])
            setScrollStep(1)
        } else {
            const id = ++requestId.current
            clearAllSelections()
            if (refresh) {
                setLoading(true)
            } else {
                setSkeleton(true)
            }
            scheduleApi
                .getVisits({
                    page: page + 1,
                    page_size: pageSize,
                    search: search,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    status: status == 'all_status' ? null : status,
                    client_ids: selectedClients?.filter(Boolean)?.length === clients?.length ? null : selectedClients?.filter(Boolean)?.join(','),
                    service_ids: selectedServices?.filter(Boolean)?.length === services?.length ? null : selectedServices?.filter(Boolean)?.join(','),
                    date_from: startDate?.format('YYYY-MM-DD'),
                    date_to: endDate?.format('YYYY-MM-DD'),
                })
                .then(({data: {visits, total}}) => {
                    if (refresh) setLoading(false)
                    if (id !== requestId.current) return
                    setRows(visits)
                    setRowCount(total)
                    setTimeout(() => {
                        if (id === requestId.current) setSkeleton(false)
                    }, 200)
                })
                .catch(error => {
                    if (refresh) setLoading(false)
                    if (id !== requestId.current) return
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                    setSkeleton(false)
                })
        }
    }

    const refreshData = () => loadData(true)

    const loadScrollData = () => {
        const id = ++scrollRequestId.current
        const refresh = scrollStep == 1 && refreshRef.current
        refreshRef.current = false
        if (branchId) {
            if (scrollStep == 1 && !refresh) {
                setSkeleton(true)
            }
            scheduleApi
                .getVisits({
                    page: scrollStep,
                    page_size: 10,
                    search: search,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    status: status == 'all_status' ? null : status,
                    client_ids: selectedClients?.filter(Boolean)?.length === clients?.length ? null : selectedClients?.filter(Boolean)?.join(','),
                    service_ids: selectedServices?.filter(Boolean)?.length === services?.length ? null : selectedServices?.filter(Boolean)?.join(','),
                    date_from: startDate?.format('YYYY-MM-DD'),
                    date_to: endDate?.format('YYYY-MM-DD'),
                })
                .then(({data: {visits}}) => {
                    if (refresh) setLoading(false)
                    if (id !== scrollRequestId.current) return
                    if (visits) {
                        setScrollData(prev => (refresh ? visits : [...prev, ...visits]))
                    }
                    setHasMoreScroll(visits?.length > 0)
                    setScrollStep(prev => ++prev)
                    if (scrollStep == 1) {
                        setSkeleton(false)
                    }
                })
                .catch(error => {
                    if (refresh) setLoading(false)
                    if (id !== scrollRequestId.current) return
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                    if (scrollStep == 1) {
                        setSkeleton(false)
                    }
                })
        }
    }

    const clientChange = event => {
        const value = event.target.value
        if (value.includes('all')) {
            const allFilteredIds = filteredClients.map(c => c.id)
            const isAllSelected = allFilteredIds.every(id => selectedClients.includes(id))
            if (isAllSelected) {
                setSelectedClients(prev => prev.filter(id => !allFilteredIds.includes(id)))
            } else {
                setSelectedClients(prev => [...new Set([...prev, ...allFilteredIds])])
            }
        } else {
            setSelectedClients(value)
        }
    }

    const renderClientLabel = (selecteds = []) => {
        const cleanValues = selecteds.filter(Boolean)
        if (clients.length > 0 && cleanValues.length === clients.length) {
            return 'All Clients'
        }
        return clients
            .filter(r => cleanValues.includes(r.id))
            .map(r => r.display_name)
            .join(', ')
    }

    const serviceChange = event => {
        const value = event.target.value
        if (value.includes('all')) {
            const allFilteredIds = filteredServices.map(c => c.id)
            const isAllSelected = allFilteredIds.every(id => selectedServices.includes(id))
            if (isAllSelected) {
                setSelectedServices(prev => prev.filter(id => !allFilteredIds.includes(id)))
            } else {
                setSelectedServices(prev => [...new Set([...prev, ...allFilteredIds])])
            }
        } else {
            setSelectedServices(value)
        }
    }

    const renderServiceLabel = (selecteds = []) => {
        const cleanValues = selecteds.filter(Boolean)
        if (services.length > 0 && cleanValues.length === services.length) {
            return 'All Services'
        }
        return services
            .filter(r => cleanValues.includes(r.id))
            .map(r => r.display_name)
            .join(', ')
    }

    const sortChange = data => {
        if (data.length > 0) {
            const {field, sort} = data[0]
            setSortBy(field)
            setSortOrder(sort)
        } else {
            setSortBy(null)
            setSortOrder(null)
        }
    }

    const menuOpen = (e, row) => {
        if (rowSelectionModel?.ids?.size > 0 || (rowSelectionModel?.type == 'exclude' && rowSelectionModel?.ids?.size == 0)) {
            menuClose()
            clearAllSelections()
        } else {
            setAnchorEl(e.currentTarget)
            setSelectedRow(row)
            setSelectedRows([row])
        }
    }

    const menuClose = () => {
        setAnchorEl(null)
    }

    const scheduleDetails = () => {
        menuClose()
        navigate(`/scheduling/${selectedRow?.schedule_id}/visits/${selectedRow?.id}`)
    }

    const updateSchedule = () => {
        menuClose()
        navigate(`/scheduling/schedule-form/${selectedRow?.schedule_id}`)
    }

    const changeCaregiver = () => {
        menuClose()
        setOpenChangeCaregiver(true)
    }

    const markAsComplete = () => {
        menuClose()
        setOpenMarkAsComplete(true)
    }

    const cancelSchedule = () => {
        menuClose()
        setOpenCancelVisits(true)
    }

    const deleteVisit = () => {
        menuClose()
        setOpenDeleteVisits(true)
    }

    const recordClockOut = () => {
        menuClose()
        setOpenClockOut(true)
    }

    const undoClockIn = () => {
        menuClose()
        setOpenUndoClockIn(true)
    }

    const sendSignReminderToCaregiver = () => {
        menuClose()
        setOpenSendSignatureReminder(true)
    }

    const resetCaregiverSignature = () => {
        menuClose()
    }

    const updateScheduleReport = () => {
        menuClose()
    }

    const downloadReport = () => {
        menuClose()
    }

    const claimBillingToDHS = () => {
        menuClose()
    }

    const resetSignature = () => {
        menuClose()
    }

    const reschedule = () => {
        menuClose()
        setOpenRescheduleVisits(true)
    }

    const generatePdfReport = () => {
        menuClose()
    }

    const mobileReset = () => {
        setSelectedClients([])
        setSelectedServices([])
        setSortBy('')
        setSortOrder('')
        setScrollData([])
        setScrollStep(1)
    }

    const selectAll = () => {
        setRowSelectionModel({
            type: 'include',
            ids: new Set(rows.map(row => row.id)),
        })
    }

    const clearAllSelections = () => {
        setRowSelectionModel({
            type: 'include',
            ids: new Set(),
        })
    }

    const onRowClick = row => {
        navigate(`/scheduling/${row?.schedule_id}/visits/${row?.id}`)
    }

    const columns = [
        {
            field: 'client',
            headerName: 'Client',
            flex: 1.4,
            valueGetter: (value, row) => row?.schedule?.client?.first_name + ' ' + row?.schedule?.client?.last_name,
            renderCell: ({row}) => (
                <div className="people-card">
                    {row?.schedule?.client?.avatar_url ? (
                        <Avatar src={row?.schedule?.client?.avatar_url} style={{width: 32, height: 32}} />
                    ) : (
                        <Avatar
                            style={{
                                backgroundColor: getColorFromName(row?.schedule?.client?.first_name + ' ' + row?.schedule?.client?.last_name),
                                width: 32,
                                height: 32,
                                fontSize: 16,
                            }}>
                            {getInitials(row?.schedule?.client?.first_name + ' ' + row?.schedule?.client?.last_name)}
                        </Avatar>
                    )}
                    <div className="people-profile-card">
                        <Typography variant="body2" color="text.primary" className="name">
                            {row?.schedule?.client?.first_name + ' ' + row?.schedule?.client?.last_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" className="name">
                            {row?.schedule?.client?.email}
                        </Typography>
                    </div>
                </div>
            ),
        },
        {
            field: 'caregiver',
            headerName: 'Caregiver',
            flex: 1.4,
            valueGetter: (value, row) => {
                return row?.caregiver?.first_name + ' ' + row?.caregiver?.last_name
            },
            renderCell: ({row}) => (
                <div className="people-card">
                    {row?.caregiver?.avatar_url ? (
                        <Avatar src={row?.caregiver?.avatar_url} style={{width: 32, height: 32}} />
                    ) : (
                        <Avatar
                            style={{
                                backgroundColor: getColorFromName(row?.caregiver?.first_name + ' ' + row?.caregiver?.last_name),
                                width: 32,
                                height: 32,
                                fontSize: 16,
                            }}>
                            {getInitials(row?.caregiver?.first_name + ' ' + row?.caregiver?.last_name)}
                        </Avatar>
                    )}
                    <Typography variant="body2" color="text.primary" className="name">
                        {row?.caregiver?.first_name + ' ' + row?.caregiver?.last_name}
                    </Typography>
                </div>
            ),
        },
        {
            field: 'service',
            headerName: 'Service',
            flex: 1,
            valueGetter: (value, row) => row?.schedule?.service?.name,
            renderCell: ({row}) => row?.schedule?.service?.name,
        },
        {
            field: 'date',
            headerName: 'Schedule Time',
            flex: 1,
            renderCell: ({row}) => (
                <div className="schedule">
                    <Typography variant="h9" color="text.primary">
                        {format(new Date(ignoreTimezone(row?.date)), 'MM/dd/yyyy')}
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                        {formatTime(row?.start_time)}-{formatTime(row?.end_time)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        ({formatTimeDiffShort(row?.start_time, row?.end_time)})
                    </Typography>
                </div>
            ),
        },
        {
            field: 'date_of_birth2',
            headerName: 'Report Time',
            flex: 1,
            renderCell: ({row}) => (
                <div className="schedule">
                    <Typography variant="h9" color="text.primary">
                        {row?.clock_in_time ? format(new Date(row?.clock_in_time), 'MM/dd/yyyy') : '-'}
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                        {row?.clock_in_time && row?.clock_out_time
                            ? `${format(new Date(row?.clock_in_time), 'h:mm a')}-${format(new Date(row?.clock_out_time), 'h:mm a')}`
                            : '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {row?.clock_in_time && row?.clock_out_time
                            ? formatTimeDiffShort(format(new Date(row?.clock_in_time), 'HH:mm'), format(new Date(row?.clock_out_time), 'HH:mm'))
                            : '-'}
                    </Typography>
                </div>
            ),
        },
        {
            field: 'total_cost',
            headerName: 'Total Cost',
            flex: 1,
            valueGetter: (value, row) => row?.total_cost,
            renderCell: ({row}) => (row?.total_cost ? '$' + row?.total_cost : ''),
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 0.8,
            renderCell: ({value}) => {
                if (value === 'scheduled') {
                    return (
                        <Chip
                            label="Scheduled"
                            variant="filled"
                            sx={theme => ({
                                backgroundColor: theme.palette.grey[700],
                                color: theme.palette.secondary.contrastText,
                            })}
                        />
                    )
                } else if (value === 'in_progress') {
                    return <Chip label="In progress" variant="filled" color="warning" />
                } else if (value === 'ended') {
                    return (
                        <Chip
                            label="Ended"
                            variant="filled"
                            sx={theme => ({
                                backgroundColor: '#02CAD1',
                                color: theme.palette.primary.contrastText,
                            })}
                        />
                    )
                } else if (value === 'finished') {
                    return <Chip label="Finished" variant="filled" color="info" />
                } else if (value === 'completed') {
                    return <Chip label="Completed" variant="filled" color="success" />
                } else if (value === 'cancelled') {
                    return <Chip label="Cancelled" variant="filled" color="error" />
                }
                return null
            },
        },
        {
            field: 'action',
            headerName: 'Action',
            flex: 0.5,
            sortable: false,
            headerAlign: 'center',
            renderCell: ({row}) => {
                let warning = ', '
                if (row?.clock_in_warnings?.includes('outside_radius')) {
                    warning += 'The clock-in location is far from the scheduled location.'
                } else if (row?.clock_out_warnings?.includes('outside_radius')) {
                    warning += 'The clock-out location is far from the scheduled location.'
                } else if (row?.clock_in_warnings?.includes('late')) {
                    warning += 'The clock-in time was late.'
                } else if (row?.clock_in_warnings?.includes('early')) {
                    warning += 'The clock-in time was too early.'
                } else if (row?.clock_out_warnings?.includes('late')) {
                    warning += 'The clock-out time was late.'
                } else if (row?.clock_out_warnings?.includes('outside_radius')) {
                    warning += 'The clock-out time was too early.'
                }

                return (
                    <div className="action-column">
                        {warning !== ', ' && (
                            <Tooltip
                                title={warning.substring(2)}
                                placement="left"
                                arrow
                                slotProps={{
                                    tooltip: {
                                        sx: {
                                            maxWidth: 173,
                                            bgcolor: '#616161',
                                            px: 1,
                                            py: 1,
                                        },
                                    },
                                    arrow: {
                                        sx: {
                                            color: '#616161',
                                        },
                                    },
                                }}>
                                <Warning fontSize="small" color="warning" style={{visibility: 'inherit'}} />
                            </Tooltip>
                        )}
                        <IconButton
                            onClick={e => {
                                e.stopPropagation()
                                menuOpen(e, row)
                            }}>
                            <MoreVert />
                        </IconButton>
                    </div>
                )
            },
        },
    ]

    return (
        <>
            <div className={mobile ? '' : 'filter'}>
                <TextField
                    placeholder="Search client or caregiver or service name ..."
                    style={{flex: 2}}
                    fullWidth={mobile}
                    onChange={e => setSearchTyping(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                {mobile ? (
                    <>
                        <br />
                        <br />
                        <RangePicker
                            value={startDate && endDate ? [startDate, endDate] : [null, null]}
                            onChange={e => {
                                const [start_date, end_date] = e
                                setStartDate(start_date)
                                setEndDate(end_date)
                            }}
                            format="MM/DD/YYYY"
                            allowClear={false}
                            style={{
                                borderRadius: 16,
                                border: '1px solid #0000003B',
                                backgroundColor: '#fbfbfb',
                                height: 56,
                                color: '#00000099',
                                width: dateRangePopup ? 600 : '100%',
                            }}
                            placement="bottomRight"
                            onOpenChange={open => setDateRangePopup(open)}
                        />
                    </>
                ) : (
                    <>
                        <FormControl style={{flex: 1}}>
                            <Select
                                multiple
                                value={selectedClients}
                                onChange={clientChange}
                                renderValue={renderClientLabel}
                                MenuProps={{
                                    PaperProps: {
                                        style: {maxHeight: 300},
                                        ref: menuRef,
                                    },
                                }}
                                onOpen={() => {
                                    setTimeout(() => {
                                        if (menuRef?.current) {
                                            menuRef.current.scrollTop = 0
                                        }
                                    }, 0)
                                }}>
                                <MenuItem disableRipple disableTouchRipple>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Search client name..."
                                        value={searchClientTerm}
                                        onChange={e => setSearchClientTerm(e.target.value)}
                                        onKeyDown={e => e.stopPropagation()}
                                        autoFocus
                                    />
                                </MenuItem>
                                <MenuItem value="all">
                                    <Checkbox
                                        checked={filteredClients.length > 0 && filteredClients.every(c => selectedClients.includes(c.id))}
                                        indeterminate={
                                            filteredClients.some(c => selectedClients.includes(c.id)) &&
                                            !filteredClients.every(c => selectedClients.includes(c.id))
                                        }
                                    />
                                    <ListItemText primary="Select All" />
                                </MenuItem>
                                {filteredClients.map(item => (
                                    <MenuItem key={item.id} value={item.id}>
                                        <Checkbox checked={selectedClients.includes(item.id)} />
                                        <div className="people-card">
                                            {item?.avatar_url ? (
                                                <Avatar src={item?.avatar_url} sx={{width: 24, height: 24}} />
                                            ) : (
                                                <Avatar
                                                    sx={{
                                                        backgroundColor: getColorFromName(item?.first_name + ' ' + item?.last_name),
                                                        width: 24,
                                                        height: 24,
                                                        fontSize: 12,
                                                    }}>
                                                    {getInitials(item?.first_name + ' ' + item?.last_name)}
                                                </Avatar>
                                            )}
                                            <Typography variant="inputText">{item?.first_name + ' ' + item?.last_name}</Typography>
                                        </div>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl style={{flex: 1}}>
                            <Select
                                multiple
                                value={selectedServices}
                                onChange={serviceChange}
                                renderValue={renderServiceLabel}
                                MenuProps={{
                                    PaperProps: {
                                        style: {maxHeight: 300},
                                        ref: menuRef,
                                    },
                                }}
                                onOpen={() => {
                                    setTimeout(() => {
                                        if (menuRef?.current) {
                                            menuRef.current.scrollTop = 0
                                        }
                                    }, 0)
                                }}>
                                <MenuItem disableRipple disableTouchRipple>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Search service name..."
                                        value={searchServiceTerm}
                                        onChange={e => setSearchServiceTerm(e.target.value)}
                                        onKeyDown={e => e.stopPropagation()}
                                        autoFocus
                                    />
                                </MenuItem>
                                <MenuItem value="all">
                                    <Checkbox
                                        checked={filteredServices.length > 0 && filteredServices.every(c => selectedServices.includes(c.id))}
                                        indeterminate={
                                            filteredServices.some(c => selectedServices.includes(c.id)) &&
                                            !filteredServices.every(c => selectedServices.includes(c.id))
                                        }
                                    />
                                    <ListItemText primary="Select All" />
                                </MenuItem>
                                {filteredServices.map(item => (
                                    <MenuItem key={item.id} value={item.id}>
                                        <Checkbox checked={selectedServices.includes(item.id)} />
                                        <div className="service-display">
                                            <Chip
                                                label={item?.billing_unit?.replace('_', ' ')?.toUpperCase()}
                                                color="action.selected"
                                                variant="filled"
                                                size="small"
                                                style={{fontWeight: 'bold', backgroundColor: '#0000000A'}}
                                            />
                                            <Typography variant="inputText">{item?.display_name}</Typography>
                                        </div>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl style={{flex: 1}}>
                            <RangePicker
                                value={startDate && endDate ? [startDate, endDate] : [null, null]}
                                onChange={e => {
                                    const [start_date, end_date] = e
                                    setStartDate(start_date)
                                    setEndDate(end_date)
                                }}
                                format="MM/DD/YYYY"
                                allowClear={false}
                                style={{
                                    borderRadius: 16,
                                    border: '1px solid #0000003B',
                                    backgroundColor: '#fbfbfb',
                                    height: 56,
                                    color: '#00000099',
                                }}
                                placement="bottomRight"
                            />
                        </FormControl>
                    </>
                )}
            </div>
            <br />
            {mobile ? (
                <>
                    <div className="mobile-filter" onClick={() => setOpenMobileFilter(true)}>
                        <FilterAlt color="primary" fontSize="medium" />
                        <Typography variant="buttonMedium" color="primary">
                            Filter
                        </Typography>
                    </div>
                    {skeleton && <VisitCardsSkeleton />}
                    {!skeleton && scrollData?.length > 0 && (
                        <InfiniteScroll
                            dataLength={scrollData?.length}
                            next={loadScrollData}
                            hasMore={hasMoreScroll}
                            loader={
                                <Box display="flex" justifyContent="center" my={2}>
                                    <CircularProgress />
                                </Box>
                            }
                            endMessage={
                                <Typography align="center" sx={{mt: 2}}>
                                    All Loaded.
                                </Typography>
                            }>
                            {scrollData.map((row, index) => (
                                <div className="card" key={index} onClick={() => onRowClick(row)}>
                                    <div className="top-card">
                                        <div className="left-card">
                                            {row?.status == 'scheduled' && (
                                                <Chip
                                                    label="Scheduled"
                                                    variant="filled"
                                                    sx={theme => ({
                                                        backgroundColor: theme.palette.grey[700],
                                                        color: theme.palette.secondary.contrastText,
                                                    })}
                                                />
                                            )}
                                            {row?.status == 'in_progress' && <Chip label="In progress" variant="filled" color="warning" />}
                                            {row?.status == 'ended' && (
                                                <Chip
                                                    label="Ended"
                                                    variant="filled"
                                                    sx={theme => ({
                                                        backgroundColor: '#02CAD1',
                                                        color: theme.palette.primary.contrastText,
                                                    })}
                                                />
                                            )}
                                            {row?.status == 'finished' && <Chip label="Finished" variant="filled" color="info" />}
                                            {row?.status == 'completed' && <Chip label="Completed" variant="filled" color="success" />}
                                            {row?.status == 'cancelled' && <Chip label="Cancelled" variant="filled" color="error" />}
                                        </div>
                                        <div className="right-card">
                                            <Tooltip
                                                title="The clock-in location is far from the scheduled location"
                                                placement="bottom"
                                                arrow
                                                slotProps={{
                                                    tooltip: {
                                                        sx: {
                                                            maxWidth: 126,
                                                            bgcolor: '#616161',
                                                            px: 1,
                                                            py: 1,
                                                        },
                                                    },
                                                    arrow: {
                                                        sx: {
                                                            color: '#616161',
                                                        },
                                                    },
                                                }}>
                                                <Warning fontSize="small" color="warning" style={{visibility: 'inherit'}} />
                                            </Tooltip>
                                            <IconButton
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    menuOpen(e, row)
                                                }}
                                                style={{marginRight: -10}}>
                                                <MoreVert />
                                            </IconButton>
                                        </div>
                                    </div>
                                    <Typography variant="body2" color="text.primary">
                                        {row?.schedule?.service?.name}
                                    </Typography>
                                    <div className="form">
                                        <Typography variant="h8" color="text.primary">
                                            Total Cost
                                        </Typography>
                                        <Typography variant="body2" color="text.primary" style={{marginTop: 2}}>
                                            {row?.schedule?.total_cost ? '$' + row?.schedule?.total_cost : ''}
                                        </Typography>
                                    </div>
                                    <Typography variant="h8" color="text.primary">
                                        Client:
                                    </Typography>
                                    <div className="people-card">
                                        {row?.schedule?.client?.avatar_url ? (
                                            <Avatar src={row?.schedule?.client?.avatar_url} style={{width: 32, height: 32}} />
                                        ) : (
                                            <Avatar
                                                style={{
                                                    backgroundColor: getColorFromName(
                                                        row?.schedule?.client?.first_name + ' ' + row?.schedule?.client?.last_name,
                                                    ),
                                                    width: 32,
                                                    height: 32,
                                                    fontSize: 16,
                                                }}>
                                                {getInitials(row?.schedule?.client?.first_name + ' ' + row?.schedule?.client?.last_name)}
                                            </Avatar>
                                        )}
                                        <div className="people-profile-card">
                                            <Typography variant="body2" color="text.primary">
                                                {row?.schedule?.client?.first_name + ' ' + row?.schedule?.client?.last_name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {row?.schedule?.client?.email}
                                            </Typography>
                                        </div>
                                    </div>
                                    <Typography variant="h8" color="text.primary">
                                        Caregiver:
                                    </Typography>
                                    <div className="people-card">
                                        {row?.caregiver?.avatar_url ? (
                                            <Avatar src={row?.caregiver?.avatar_url} style={{width: 24, height: 24}} />
                                        ) : (
                                            <Avatar
                                                style={{
                                                    backgroundColor: getColorFromName(row?.caregiver?.first_name + ' ' + row?.caregiver?.last_name),
                                                    width: 24,
                                                    height: 24,
                                                    fontSize: 12,
                                                }}>
                                                {getInitials(row?.caregiver?.first_name + ' ' + row?.caregiver?.last_name)}
                                            </Avatar>
                                        )}
                                        <Typography variant="body2" color="text.primary">
                                            {row?.caregiver?.first_name + ' ' + row?.caregiver?.last_name}
                                        </Typography>
                                    </div>
                                    <div className="form" style={{marginTop: 4}}>
                                        <div style={{width: 180}}>
                                            <Typography variant="h8" color="text.primary">
                                                Schedule Time:
                                            </Typography>
                                        </div>
                                        <div>
                                            <Typography variant="h8" color="text.primary">
                                                Report Time:
                                            </Typography>
                                        </div>
                                    </div>
                                    <div className="form" style={{marginTop: -4}}>
                                        <div style={{width: 180}}>
                                            <Typography variant="h9" color="text.primary">
                                                {format(new Date(ignoreTimezone(row?.date)), 'MM/dd/yyyy')}
                                            </Typography>
                                            <br />
                                            <Typography variant="body2" color="text.primary">
                                                {formatTime(row?.start_time)}-{formatTime(row?.end_time)} (
                                                {formatTimeDiffShort(row?.start_time, row?.end_time)})
                                            </Typography>
                                        </div>
                                        <div>
                                            <Typography variant="h9" color="text.primary">
                                                {row?.clock_in_time ? format(new Date(row?.clock_in_time), 'MM/dd/yyyy') : '-'}
                                            </Typography>
                                            <br />
                                            <Typography variant="body2" color="text.primary">
                                                {row?.clock_in_time && row?.clock_out_time
                                                    ? `${format(new Date(row?.clock_in_time), 'HH:mm')}-${format(new Date(row?.clock_out_time), 'HH:mm')}`
                                                    : '-'}{' '}
                                                {row?.clock_in_time && row?.clock_out_time
                                                    ? `(${formatTimeDiffShort(
                                                          format(new Date(row?.clock_in_time), 'HH:mm'),
                                                          format(new Date(row?.clock_out_time), 'HH:mm'),
                                                      )})`
                                                    : ''}
                                            </Typography>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </InfiniteScroll>
                    )}
                </>
            ) : (
                <>
                    {skeleton && (
                        <TableSkeleton
                            rows={Math.min(pageSize, 10)}
                            rowHeight={66}
                            headerHeight={58}
                            checkbox={status != 'all_status'}
                            columns={[
                                {flex: 1.4, type: 'avatar', lines: 2, widths: ['62%', '50%', '70%', '56%']},
                                {flex: 1.4, type: 'avatar', widths: ['54%', '66%', '48%', '60%']},
                                {flex: 1, widths: ['70%', '58%', '64%']},
                                {flex: 1, lines: 3, widths: ['72%', '80%', '40%']},
                                {flex: 1, lines: 3, widths: ['66%', '78%', '36%']},
                                {flex: 1, widths: ['36%', '30%']},
                                {flex: 0.8, type: 'chip', chipWidth: 92},
                                {flex: 0.5, type: 'actions', headerWidth: '70%'},
                            ]}
                        />
                    )}
                    {selectedRows?.length > 0 &&
                        (rowSelectionModel?.ids?.size > 0 || (rowSelectionModel?.type == 'exclude' && rowSelectionModel?.ids?.size == 0)) && (
                            <div className="selection-toolbox">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={
                                                (rowSelectionModel?.type == 'exclude' && rowSelectionModel?.ids?.size == 0) ||
                                                (rowSelectionModel?.type == 'include' &&
                                                    (rowSelectionModel?.ids?.size == pageSize || rowSelectionModel?.ids?.size == rows?.length))
                                            }
                                            onChange={e => (e.target.checked ? selectAll() : clearAllSelections())}
                                        />
                                    }
                                    label={
                                        <Typography variant="h8" color="text.primary">
                                            Select All
                                        </Typography>
                                    }
                                />
                                {status === 'scheduled' && (
                                    <div className="selection-toolbox-options">
                                        <Button variant="contained" color="error" onClick={cancelSchedule} disabled={!editable}>
                                            Cancel Schedule
                                        </Button>
                                        <Button variant="contained" color="error" onClick={deleteVisit} disabled={!editable}>
                                            Delete Visit
                                        </Button>
                                        <Button variant="outlined" onClick={changeCaregiver} disabled={!editable}>
                                            Change Caregiver
                                        </Button>
                                        <Button variant="contained" onClick={markAsComplete} disabled={!editable}>
                                            Mark as Complete
                                        </Button>
                                    </div>
                                )}
                                {status === 'in_progress' && (
                                    <div className="selection-toolbox-options">
                                        <Button variant="contained" color="error" onClick={cancelSchedule} disabled={!editable}>
                                            Cancel Schedule
                                        </Button>
                                        <Button variant="outlined" color="error" onClick={undoClockIn} disabled={!editable}>
                                            Undo Clock-In
                                        </Button>
                                        <Button variant="outlined" onClick={recordClockOut} disabled={!editable}>
                                            Record Clock-Out
                                        </Button>
                                        <Button variant="contained" onClick={markAsComplete} disabled={!editable}>
                                            Mark as Complete
                                        </Button>
                                    </div>
                                )}
                                {status === 'ended' && (
                                    <div className="selection-toolbox-options">
                                        <Button variant="outlined" onClick={sendSignReminderToCaregiver} disabled={!editable}>
                                            Send Sign Reminder to Caregiver
                                        </Button>
                                        <Button variant="contained" onClick={markAsComplete} disabled={!editable}>
                                            Mark as Complete
                                        </Button>
                                    </div>
                                )}
                                {status === 'finished' && (
                                    <div className="selection-toolbox-options">
                                        <Button variant="outlined" onClick={resetCaregiverSignature} disabled={!editable}>
                                            Reset Caregiver Signature
                                        </Button>
                                        <Button variant="contained" onClick={markAsComplete} disabled={!editable}>
                                            Mark as Complete
                                        </Button>
                                    </div>
                                )}
                                {status === 'completed' && (
                                    <div className="selection-toolbox-options">
                                        <Button variant="outlined" onClick={generatePdfReport} disabled={!editable}>
                                            Generate PDF Report
                                        </Button>
                                        <Button variant="contained" onClick={claimBillingToDHS} disabled={!editable}>
                                            Claim Billing to DHS
                                        </Button>
                                    </div>
                                )}
                                {status === 'cancelled' && (
                                    <div className="selection-toolbox-options">
                                        <Button variant="outlined" onClick={reschedule} disabled={!editable}>
                                            Reschedule
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    {rows?.length > 0 && (
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            paginationModel={{page, pageSize}}
                            onSortModelChange={sortChange}
                            disableRowSelectionOnClick
                            disableColumnMenu
                            getRowHeight={() => 66}
                            style={{display: skeleton ? 'none' : undefined}}
                            columnHeaderHeight={
                                rowSelectionModel?.ids?.size > 0 || (rowSelectionModel?.type == 'exclude' && rowSelectionModel?.ids?.size == 0)
                                    ? 0
                                    : 58
                            }
                            checkboxSelection={status != 'all_status'}
                            rowSelectionModel={rowSelectionModel}
                            onRowSelectionModelChange={model => setRowSelectionModel(model)}
                            onRowClick={e => onRowClick(e.row)}
                            slots={{
                                footer: () => (
                                    <DataGridFooter page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} rowCount={rowCount} />
                                ),
                            }}
                        />
                    )}
                </>
            )}
            {!skeleton && ((!mobile && rows?.length == 0) || (mobile && scrollData?.length == 0)) && (
                <>
                    <br />
                    <Typography variant="subtitle2" color="text.secondary">
                        Search Results (0)
                    </Typography>
                    <br />
                    <br />
                    <div style={{textAlign: 'center'}}>
                        <img src={noResultsFound} width={mobile ? '200' : '372'} />
                        <br />
                        <br />
                        <div style={{marginBottom: 10}}>
                            <Typography variant={mobile ? 'h7' : 'h5'} color="text.primary">
                                No Results Found
                            </Typography>
                        </div>
                        <Typography variant={mobile ? 'body2' : 'body1'} color="text.primary">
                            No schedule match your search criteria. Try adjusting your filters or criteria to find schedules.
                        </Typography>
                    </div>
                </>
            )}
            <br />
            <br />
            <br />
            <br />
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={menuClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}>
                {selectedRow?.status == 'scheduled' && (
                    <div>
                        <MenuItem onClick={scheduleDetails}>Schedule Details</MenuItem>
                        <MenuItem onClick={updateSchedule}>Update Schedule</MenuItem>
                        <MenuItem onClick={changeCaregiver}>Change Caregiver</MenuItem>
                        <MenuItem onClick={markAsComplete}>Mark as Complete</MenuItem>
                        <MenuItem onClick={cancelSchedule} sx={theme => ({color: theme.palette.error.main})}>
                            Cancel Schedule
                        </MenuItem>
                        <MenuItem onClick={deleteVisit} sx={theme => ({color: theme.palette.error.main})}>
                            Delete Visit
                        </MenuItem>
                    </div>
                )}
                {selectedRow?.status == 'in_progress' && (
                    <div>
                        <MenuItem onClick={scheduleDetails}>Schedule Details</MenuItem>
                        <MenuItem onClick={updateSchedule}>Update Schedule</MenuItem>
                        <MenuItem onClick={recordClockOut}>Record Clock-Out</MenuItem>
                        <MenuItem onClick={markAsComplete}>Mark as Complete</MenuItem>
                        <MenuItem onClick={undoClockIn} sx={theme => ({color: theme.palette.error.main})}>
                            Undo Clock-In
                        </MenuItem>
                        <MenuItem onClick={cancelSchedule} sx={theme => ({color: theme.palette.error.main})}>
                            Cancel Schedule
                        </MenuItem>
                    </div>
                )}
                {selectedRow?.status == 'ended' && (
                    <div>
                        <MenuItem onClick={scheduleDetails}>Schedule Details</MenuItem>
                        <MenuItem onClick={updateSchedule}>Update Schedule</MenuItem>
                        <MenuItem onClick={sendSignReminderToCaregiver}>Send Sign Reminder to Caregiver</MenuItem>
                        <MenuItem onClick={markAsComplete}>Mark as Complete</MenuItem>
                        <MenuItem onClick={cancelSchedule} sx={theme => ({color: theme.palette.error.main})}>
                            Cancel Schedule
                        </MenuItem>
                    </div>
                )}
                {selectedRow?.status == 'finished' && (
                    <div>
                        <MenuItem onClick={scheduleDetails}>Schedule Details</MenuItem>
                        <MenuItem onClick={updateSchedule}>Update Schedule</MenuItem>
                        <MenuItem onClick={markAsComplete}>Mark as Complete</MenuItem>
                        <MenuItem onClick={resetCaregiverSignature}>Reset Caregiver Signature</MenuItem>
                        <MenuItem onClick={cancelSchedule} sx={theme => ({color: theme.palette.error.main})}>
                            Cancel Schedule
                        </MenuItem>
                    </div>
                )}
                {selectedRow?.status == 'completed' && (
                    <div>
                        <MenuItem onClick={scheduleDetails}>Schedule Details</MenuItem>
                        <MenuItem onClick={updateScheduleReport}>Update Schedule Report</MenuItem>
                        <MenuItem onClick={downloadReport}>Download Report</MenuItem>
                        <MenuItem onClick={claimBillingToDHS}>Claim Billing to DHS</MenuItem>
                        <MenuItem onClick={resetSignature}>Reset Signature</MenuItem>
                    </div>
                )}
                {selectedRow?.status == 'cancelled' && (
                    <div>
                        <MenuItem onClick={scheduleDetails}>Schedule Details</MenuItem>
                        <MenuItem onClick={reschedule}>Reschedule</MenuItem>
                    </div>
                )}
            </Menu>
            <MobileFilter
                open={openMobileFilter}
                setOpen={setOpenMobileFilter}
                setSortBy={setSortBy}
                setSortOrder={setSortOrder}
                mobileReset={mobileReset}
            />
            <MarkAsComplete open={openMarkAsComplete} setOpen={setOpenMarkAsComplete} loadData={refreshData} items={selectedRows} />
            <DeleteVisits open={openDeleteVisits} setOpen={setOpenDeleteVisits} loadData={refreshData} items={selectedRows} />
            <CancelVisits open={openCancelVisits} setOpen={setOpenCancelVisits} loadData={refreshData} items={selectedRows} />
            <RescheduleVisits open={openRescheduleVisits} setOpen={setOpenRescheduleVisits} loadData={refreshData} items={selectedRows} />
            <ClockOut open={openClockOut} setOpen={setOpenClockOut} loadData={refreshData} items={selectedRows} />
            <UndoClockIn open={openUndoClockIn} setOpen={setOpenUndoClockIn} loadData={refreshData} items={selectedRows} />
            <SendSignatureReminder
                open={openSendSignatureReminder}
                setOpen={setOpenSendSignatureReminder}
                loadData={refreshData}
                items={selectedRows}
            />
            <ChangeCaregiver open={openChangeCaregiver} setOpen={setOpenChangeCaregiver} loadData={refreshData} items={selectedRows} />
        </>
    )
}
